/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module UpdateModule */

import { ComponentStore } from '@ngrx/component-store';
import { Image } from 'ajs/modules/upgrade/factories/image.item.factory';
import { Injectable } from '@angular/core';
import { ITSEGroup } from 'ajs/modules/service-engine-group/factories/se-group.item.factory';
import { ITUpgradeNode } from 'ajs/modules/upgrade/factories/upgrade-node.item.factory';
import { Observable, Subject } from 'rxjs';
import { UpdateService } from 'ng/modules/update/services/update.service';
import { UpgradeFsmState } from 'generated-types';

import { IUserSelectedOptions, UpgradeType } from '../update.types';

interface IUpgradeState {
    isSegUpgrade: boolean;
    preChecksTriggered: boolean;
    preChecksCompleted: boolean;
    upgradeCollectionLoaded: boolean;
    isPreCheckFlow: boolean;
    isPreCheckOnlyFlow: boolean;
    isSystemUpdateInProgress: boolean;
    isUpgradeTriggeredFromSystemUpdatePage: boolean;
    isUpdateTriggeredWithWarning: boolean;
    overallPreChecksState?: UpgradeFsmState;
    preCheckNodes?: ITUpgradeNode[];
    userSelectedOptions: IUserSelectedOptions;
    rollbackVersion: string;
    selectedSegs?: ITSEGroup[];
    selectedSegUpgradeImages?: Image[];
    isRestoreFlow?: boolean;
    isDryRunFlow?: boolean;
    dryRunNodes: ITUpgradeNode[];
}

const INITIAL_USER_SELECTED_OPTIONS: IUserSelectedOptions = {};

const INITIAL_UPGRADE_STATE: IUpgradeState = {
    isSegUpgrade: false,
    isPreCheckFlow: false,
    isPreCheckOnlyFlow: false,
    preChecksTriggered: false,
    preChecksCompleted: false,
    preCheckNodes: [],
    upgradeCollectionLoaded: false,
    userSelectedOptions: INITIAL_USER_SELECTED_OPTIONS,
    rollbackVersion: '',
    selectedSegs: [],
    selectedSegUpgradeImages: [],
    isUpgradeTriggeredFromSystemUpdatePage: false,
    isSystemUpdateInProgress: false,
    isUpdateTriggeredWithWarning: false,
    isRestoreFlow: false,
    isDryRunFlow: false,
    dryRunNodes: [],
};

interface IPreChecksState {
    preChecksTriggered: boolean,
    preChecksCompleted: boolean,
    upgradeCollectionLoaded?: boolean,
    overallPreChecksState?: UpgradeFsmState,
}

/**
 * Type for config required to open pre-check progress modal.
 */
export interface IPrecheckModalConfig {
    readonly: boolean,
    nodeIds: string[],
}

/**
 * @description The Update store manages states commonly used by all the
 *  components in the Update module.
 * @author Sarthak Kapoor, Nisar Nadaf, Deepak Chaudhary
 */
@Injectable()
export class UpdateStore extends ComponentStore<IUpgradeState> {
    /**
     * Return if pre-checks triggered from state.
     */
    public readonly preChecksTriggered$ = this.select(state => state.preChecksTriggered);

    /**
     * Return if pre-checks completed from state.
     */
    public readonly preChecksCompleted$ = this.select(state => state.preChecksCompleted);

    /**
     * Return true on successful load of upgrade node collection.
     */
    public readonly onUpgradeCollectionLoad$ = this.select(state => state.upgradeCollectionLoaded);

    /**
     * Return if Service Engine Groups will be upgraded/roll backed as well along with Controller.
     */
    public readonly isSegUpgrade$ = this.select(state => state.isSegUpgrade);

    /**
     * Return if the last operation was the pre-check flow or actual upgrade/rollback.
     */
    public readonly isPreCheckFlow$ = this.select(state => state.isPreCheckFlow);

    /**
     * Hold the latest values, user selected in the update modal.
     */
    public readonly userSelectedOptions$ = this.select(state => state.userSelectedOptions);

    /**
     * Hold the latest value of overall status of pre-checks.
     */
    public readonly overallPreChecksState$ = this.select(state => state.overallPreChecksState);

    /**
     * Hold the updated list of nodes received on completion of pre-checks.
     */
    public readonly preCheckNodes$ = this.select(state => state.preCheckNodes);

    /**
     * Hold the selected rollback version.
     */
    public readonly rollbackVersion$ = this.select(state => state.rollbackVersion);

    /**
     * Hold the updated list of selected segs for upgrade.
     */
    public readonly selectedSegs$ = this.select(state => state.selectedSegs);

    /**
     * Hold the updated list of selected Seg upgrade images.
     */
    public readonly selectedSegUpgradeImages$ = this.select(
        state => state.selectedSegUpgradeImages,
    );

    /**
     * To identify the page from where the latest update operation was triggered.
     */
    public readonly isUpgradeTriggeredFromSystemUpdatePage$ = this.select(
        state => state.isUpgradeTriggeredFromSystemUpdatePage,
    );

    /**
     * To identify if the system update is in progress.
     */
    public readonly isSystemUpdateInProgress$ = this.select(
        state => state.isSystemUpdateInProgress,
    );

    /**
     * State variable to identify is the flow is pre-check only flow.
     */
    public readonly isPreCheckOnlyFlow$ = this.select(
        state => state.isPreCheckOnlyFlow,
    );

    /**
     * To identify if the system update is in progress.
     */
    public readonly isUpdateTriggeredWithWarning$ = this.select(
        state => state.isUpdateTriggeredWithWarning,
    );

    /**
     * To identify if the flow is Restore flow.
     */
    public readonly isRestoreFlow$ = this.select(
        state => state.isRestoreFlow,
    );

    /**
     * To identify if the flow is Dry run flow.
     */
    public readonly isDryRunFlow$ = this.select(
        state => state.isDryRunFlow,
    );

    /**
     * Hold the updated list of nodes received post dry run operation.
     */
    public readonly dryRunNodes$ = this.select(
        state => state.dryRunNodes,
    );

    /**
     * Subject to update the pre-check list on successful load.
     */
    private preChecksUpdate$ = new Subject<void>();

    /**
     * Open the pre-check transcript modal from right rail card.
     */
    private openPreCheckTranscript$ = new Subject<IPrecheckModalConfig>();

    /**
     * Set Pre-checks state on trigger/completion of pre-checks.
     */
    private setPreChecksState = this.updater((state, preChecksState: IPreChecksState) => {
        return {
            ...state,
            ...preChecksState,
        };
    });

    /**
     * Set the isPreCheckOnly state.
     */
    public setIsPreCheckOnlyFlowState = this.updater(
        (state, isPreCheckOnlyFlow: boolean) => {
            return {
                ...state,
                isPreCheckOnlyFlow,
            };
        },
    );

    /**
     * Reset the isPreCheckOnFlowState.
     */
    public resetIsPreCheckOnlyFlowState = this.updater(
        state => {
            return {
                ...state,
                isPreCheckOnlyFlow: false,
            };
        },
    );

    constructor(
        private readonly updateService: UpdateService,
    ) {
        super(INITIAL_UPGRADE_STATE);
    }

    /**
     * Set Pre-checks state on trigger of pre-checks.
     */
    public onPreChecksTrigger(): void {
        this.setPreChecksState({
            preChecksTriggered: true,
            preChecksCompleted: false,
            upgradeCollectionLoaded: false,
        });
    }

    /**
     * Set Pre-checks state on completion of pre-checks.
     */
    public onPreChecksComplete(overallPreChecksState: UpgradeFsmState): void {
        this.setPreChecksState({
            preChecksTriggered: false,
            preChecksCompleted: true,
            upgradeCollectionLoaded: false,
            overallPreChecksState,
        });
    }

    /**
     * Set upgradeCollectionLoaded state on successful load for the first time
     * after pre-checks are completed.
     */
    private setUpgradeCollectionLoadedState = this.updater(
        (state, upgradeCollectionLoaded: boolean) => {
            return {
                ...state,
                upgradeCollectionLoaded,
            };
        },
    );

    /**
     * Returns an observable to allow subscriptions to pre-checks change.
     */
    public get preChecksUpdate(): Observable<void> {
        return this.preChecksUpdate$.asObservable();
    }

    /**
     * Called when the Pre-check status response has loaded.
     */
    public onPreChecksLoad(): void {
        this.preChecksUpdate$.next();
    }

    /**
     * Return an observable to allow subscriptions to pre-check transcript modal open event.
     */
    public get preCheckTranscriptOpen$(): Observable<IPrecheckModalConfig> {
        return this.openPreCheckTranscript$.asObservable();
    }

    /**
     * Open pre-check transcript modal.
     */
    public openPreCheckTranscript(preCheckConfig: IPrecheckModalConfig): void {
        this.openPreCheckTranscript$.next(preCheckConfig);
    }

    /**
     * Sets the isSegUpgrade state.
     * This state is used to determine if the Seg upgrade checkbox is selected.
     * @param isSegUpgrade - if true, it is Seg upgrade, else it is not.
     */
    public readonly setIsSegUpgrade = this.updater(
        (state, isSegUpgrade: boolean) => {
            return {
                ...state,
                isSegUpgrade,
            };
        },
    );

    /**
     * Sets the isPreCheckFlow state.
     * This state is used to determine if we are in pre-check flow or upgrade flow.
     * @param isPreCheckFlow - if true, it is pre-check flow, else it is upgrade flow.
     */
    public readonly setIsPreCheckFlowState = this.updater(
        (state, isPreCheckFlow: boolean) => {
            return {
                ...state,
                isPreCheckFlow,
            };
        },
    );

    /**
     * Sets the user selected options in the store.
     * The user selected options are the options selected by the user
     * for the upgrade/rollback operation.
     * @param userSelectedOptions - the user selected options.
     */
    public readonly setUserSelectedOptionsState = this.updater(
        (state, userSelectedOptions: IUserSelectedOptions) => {
            return {
                ...state,
                userSelectedOptions,
            };
        },
    );

    /**
     * Resets the preChecksTriggered state.
     * This state is used to determine if the pre-checks have been triggered.
     * @param preChecksTriggered - if true, the pre-checks have been triggered, else not.
     */
    public readonly resetPreChecksTriggeredState = this.updater(
        (state, preChecksTriggered: boolean) => {
            return {
                ...state,
                preChecksTriggered,
            };
        },
    );

    /**
     * Sets the pre-check nodes in the store.
     * The pre-check nodes are the nodes that have been selected for the upgrade/rollback operation.
     * @param preCheckNodes - the pre-check nodes.
     */
    public readonly setPreCheckNodesState = this.updater((state, preCheckNodes: ITUpgradeNode[]) => {
        return {
            ...state,
            preCheckNodes: [...preCheckNodes],
        };
    });

    /**
     * Resets the preChecksCompleted state.
     * This state is used to determine if the pre-checks have been completed.
     * @param preChecksCompleted - if true, the pre-checks have been completed, else not.
     */
    public readonly resetPreChecksCompletedState = this.updater(
        (state, preChecksCompleted: boolean) => {
            return {
                ...state,
                preChecksCompleted,
            };
        },
    );

    /**
     * Set the isRestoreFlow state.
     */
    public readonly setIsRestoreFlowState = this.updater(
        (state, isRestoreFlow: boolean) => {
            return {
                ...state,
                isRestoreFlow,
            };
        },
    );

    /**
     * Trigger pre-checks for upgrade/rollback operation.
     */
    public triggerPreChecks(
        preCheckOptions: IUserSelectedOptions,
        skipWarnings: boolean,
    ): Promise<void> {
        const {
            UPGRADE_TYPE_SYSTEM_UPGRADE,
            UPGRADE_TYPE_SEG_PRECHECK,
            UPGRADE_TYPE_SEG_UPGRADE,
            UPGRADE_TYPE_SYSTEM_ROLLBACK,
            UPGRADE_TYPE_SYSTEM_DRYRUN,
            UPGRADE_TYPE_SYSTEM_PRECHECK,
            UPGRADE_TYPE_SYSTEM_RESTORE,
        } = UpgradeType;

        const {
            upgradeConfig,
            applyToSystem = preCheckOptions.upgradeType !== UPGRADE_TYPE_SYSTEM_RESTORE,
            actionOnSegFailure,
            upgradeType,
            dryrun = preCheckOptions.upgradeType === UPGRADE_TYPE_SYSTEM_DRYRUN,
            file,
            passphrase,
        } = preCheckOptions;
        const preCheckOnly = preCheckOptions.upgradeType === UPGRADE_TYPE_SYSTEM_PRECHECK ||
            preCheckOptions.upgradeType === UPGRADE_TYPE_SEG_PRECHECK;

        switch (upgradeType) {
            case UPGRADE_TYPE_SYSTEM_RESTORE:
                return this.updateService.startSystemRestore(
                    skipWarnings,
                    file,
                    passphrase,
                );
            case UPGRADE_TYPE_SYSTEM_UPGRADE:
            case UPGRADE_TYPE_SYSTEM_DRYRUN:
            case UPGRADE_TYPE_SYSTEM_PRECHECK:
                if (preCheckOptions.isRollback) {
                    return this.updateService.startSystemRollback(
                        upgradeConfig,
                        skipWarnings,
                        applyToSystem,
                        preCheckOnly,
                    );
                } else {
                    return this.updateService.startSystemUpgrade(
                        upgradeConfig,
                        skipWarnings,
                        applyToSystem,
                        actionOnSegFailure,
                        dryrun,
                        preCheckOnly,
                    );
                }

            case UPGRADE_TYPE_SEG_PRECHECK:
            case UPGRADE_TYPE_SEG_UPGRADE:
                return this.updateService.startSegUpgrade(
                    upgradeConfig,
                    skipWarnings,
                    actionOnSegFailure,
                    preCheckOnly,
                );

            case UPGRADE_TYPE_SYSTEM_ROLLBACK:
                return this.updateService.startSystemRollback(
                    upgradeConfig,
                    skipWarnings,
                    applyToSystem,
                );
            default: return Promise.reject();
        }
    }

    /**
     * Update the rollback version.
     * @param rollbackVersion - The rollback version.
     */
    public readonly setRollbackVersion = this.updater(
        (state, rollbackVersion: string) => {
            return {
                ...state,
                rollbackVersion,
            };
        },
    );

    /**
     * Set the selected SEGs list.
     * @param selectedSegsList - List of selected SEGs.
     */
    public readonly setSelectedSegsListState = this.updater(
        (state, selectedSegsList: ITSEGroup[]) => {
            return {
                ...state,
                selectedSegs: [...selectedSegsList],
            };
        },
    );

    /**
     * Set the selected SEG upgrade images.
     * @param selectedSegUpgradeImages - List of selected SEG upgrade images.
     */
    public readonly setSelectedSegUpgradeImagesState = this.updater(
        (state, selectedSegUpgradeImages: Image[]) => {
            return {
                ...state,
                selectedSegUpgradeImages: [...selectedSegUpgradeImages],
            };
        },
    );

    /**
     * Update the isSystemUpdatePage state.
     * @param isSystemUpdatePage - flag indicating if the current page is system update page.
     */
    public readonly setIsSystemUpdatePageState = this.updater(
        (state, isSystemUpdatePage: boolean) => {
            return {
                ...state,
                isUpgradeTriggeredFromSystemUpdatePage: isSystemUpdatePage,
            };
        },
    );

    /**
     * Set the overallPreChecks state.
     */
    public readonly setOverallPreChecksState = this.updater(
        (state, overallPreChecksState?: UpgradeFsmState) => {
            return {
                ...state,
                overallPreChecksState,
            };
        },
    );

    /**
     * Reset overall Pre-checks State.
     */
    public readonly resetOverallPreChecksState = this.updater(
        state => {
            return {
                ...state,
                overallPreChecksState: undefined,
            };
        },
    );

    /**
     * Updates the isSystemUpdateInProgress state.
     *
     * @param isSystemUpdateInProgress - boolean flag indicating if
     * the system update is in progress.
     */
    public readonly setIsSystemUpdateInProgressState = this.updater(
        (state, isSystemUpdateInProgress: boolean) => {
            return {
                ...state,
                isSystemUpdateInProgress,
            };
        },
    );

    /**
     * Sets the isUpdateTriggeredWithWarning state.
     *
     * @param isUpdateTriggeredWithWarning - boolean flag indicating if
     * the upgrade was triggered with warnings.
     */
    public readonly setIsUpdateTriggeredWithWarningState = this.updater(
        (state, isUpdateTriggeredWithWarning: boolean) => {
            return {
                ...state,
                isUpdateTriggeredWithWarning,
            };
        },
    );

    /**
     * Set the isDryRunFlow state.
     */
    public readonly setIsDryRunFlowState = this.updater(
        (state, isDryRunFlow: boolean) => {
            return {
                ...state,
                isDryRunFlow,
            };
        },
    );

    /**
     * Set the DryRun Nodes state.
     */
    public readonly setDryRunNodesState = this.updater(
        (state, dryRunNodes: ITUpgradeNode[]) => {
            return {
                ...state,
                dryRunNodes,
            };
        },
    );
}
